import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import EventImage from "@/components/EventImage";
import { Hammer, Sparkles, Megaphone, ArrowRight } from "lucide-react";

const CAT_FR = { artisan: "Artisan", artiste: "Artiste", musicien: "Musicien", groupe: "Groupe", media: "Médias", tattoo: "Tatoueur / Tattoo artist", photographe: "Photographe", videaste: "Vidéaste", conferencier: "Conférencier", animateur: "Animateur", reconstituteur: "Reconstituteur", food_truck: "Food truck", brasserie: "Brasserie", hydromellerie: "Hydromellerie", prestataire: "Prestataire", boutique: "Boutique", createur: "Créateur", autre: "Autre" };

export const FeatureBadge = ({ type }) => {
  if (type === "sponsored")
    return <span data-testid="feature-badge-sponsored" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-stone-400 bg-stone-800/70 border border-stone-700/60 px-2 py-0.5 rounded-full"><Megaphone className="w-3 h-3" /> Sponsorisé</span>;
  return <span data-testid="feature-badge-editorial" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-amber-300 bg-amber-950/40 border border-amber-800/50 px-2 py-0.5 rounded-full"><Sparkles className="w-3 h-3" /> Découverte</span>;
};

export default function FeaturedPros({ placement, limit = 6, title = "Artisans & artistes à découvrir", subtitle }) {
  const { data: pros = [] } = useQuery({
    queryKey: ["featuredPros", placement, limit],
    queryFn: async () => (await api.get("/pro/featured", { params: { placement, limit } })).data,
  });
  if (!pros.length) return null;

  return (
    <section data-testid="featured-pros" className="my-4">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-serif text-2xl sm:text-3xl text-amber-100 flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> {title}</h2>
          {subtitle && <p className="text-sm text-stone-400 mt-1 max-w-xl">{subtitle}</p>}
        </div>
        <Link to="/professionnels" data-testid="featured-pros-all" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-stone-400 hover:text-amber-400 shrink-0">Tous les pros <ArrowRight className="w-4 h-4" /></Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {pros.map((p) => (
          <Link key={p.slug} to={`/professionnels/${p.slug}`} data-testid={`featured-pro-${p.slug}`} className="group flex flex-col bg-[#121418] border border-amber-900/30 hover:border-amber-500/50 rounded-xl p-6 transition-all hover:-translate-y-1">
            <div className="flex items-center justify-between gap-2 mb-3">
              <FeatureBadge type={p.feature_type} />
              {p.boost_premium && <span className="text-[10px] font-mono uppercase tracking-wider text-amber-200">Premium</span>}
            </div>
            <div className="flex items-center gap-3">
              {p.logo ? <img src={p.logo} alt={p.name} className="w-12 h-12 rounded-lg object-cover" /> : <span className="w-12 h-12 rounded-lg bg-amber-900/40 flex items-center justify-center"><Hammer className="w-5 h-5 text-amber-400" /></span>}
              <div className="min-w-0">
                <div className="font-serif text-lg text-stone-100 group-hover:text-amber-100 leading-tight truncate">{p.name}</div>
                <div className="font-mono text-[11px] text-stone-500 truncate">{(p.categories || []).map((c) => CAT_FR[c] || c).join(" · ")}</div>
              </div>
            </div>
            {p.description && <p className="text-sm text-stone-400 line-clamp-2 mt-3">{p.description}</p>}
          </Link>
        ))}
      </div>
    </section>
  );
}
